import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { inventoryService } from '../../../services/inventoryService';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { workOrderService } from '../../../services/workOrderService';
import { artisanProfileService, type ArtisanProfileListItemDTO } from '../../../services/artisanProfileService';
import { calculateAllocation } from '../../../utils/lotAllocation';
import type { InventoryItem } from '../../../types/inventory';
import type { Machine } from '../../../types/machine';
import type { Roaster } from '../../../types/roaster';
import type { Recipe } from '../../../types/recipe';
import type { CreateWorkOrderData, WorkOrder } from '../../../types/workOrder';
import type { RoastData, RoastProfileRow, RoastStageState } from '../../roasts/component/types';
import type { RoastStage } from '../../roasts/component/StopWatch';
import { basicInfoSchema, type BasicInfoFormData } from '../schemas/workOrderSchema';
import { buildLotOptionName, fetchWorkOrderReferenceData } from './workOrderReferenceData';
import { useLanguage } from '../../../features/language/useLanguage';
import { publishStopwatchTick, resetStopwatchTick } from '../../roasts/component/stopwatchTick';
import { resolveWorkOrderRoastType } from '../../../constants/roastTypes';

export interface LotRow {
  lotId: string;
  remaining: number;
  usedWeight: number | undefined;
  originalUsedWeight?: number;
}

export const WEIGHT_TOLERANCE = 0;
export const OUTPUT_EXCEEDS_INPUT_MESSAGE = 'workOrders.form.errors.outputExceedsInput';

export const defaultStages: RoastStageState[] = [
  { key: 'start', label: 'start', time: '00:00', temp: '' },
  { key: 'turning_point', label: 'turning_point', time: '', temp: '' },
  { key: 'yellow', label: 'yellow', time: '', temp: '' },
  { key: 'first_crack', label: 'first_crack', time: '', temp: '' },
  { key: 'end', label: 'end', time: '', temp: '' },
];

export type PendingSubmitData = {
  basicInfo: BasicInfoFormData;
  validLots: LotRow[];
  inputWeight: number;
  outputWeight: number;
  totalUsedFromLots: number;
  outputExpiryDate?: string;
};

function isWorkOrder(data: Partial<CreateWorkOrderData> | Partial<WorkOrder>): data is Partial<WorkOrder> {
  return 'id' in data || 'status' in data;
}

export const useWorkOrderForm = ({
  initialData,
  onSubmit,
}: {
  initialData?: Partial<CreateWorkOrderData> | Partial<WorkOrder>;
  onSubmit: (data: CreateWorkOrderData, action: 'draft' | 'schedule' | 'complete') => void;
}) => {
  const { t } = useLanguage();
  const onboardingActive = useOnboardingStore((s) => s.state?.status === 'in_progress');
  // Step 3 is the guided practice roast. Its completion gate only accepts a
  // work order with status 'completed', so the actions bar collapses to a single
  // "Save as Complete" on that step (see WorkOrderActionsBar).
  const onboardingRoastStep = useOnboardingStore(
    (s) => s.state?.status === 'in_progress' && s.state?.currentStep === 3,
  );
  // The sample-only pickers belong to the guided practice roast (step 3) and to
  // nothing else. Gating them on "a tour is running" instead meant that anyone who
  // opened /work-orders to do real work mid-tour got a bean dropdown holding only
  // the tagged sample bean — their own green beans, roasters and machines were
  // filtered out of a list the server had already returned in full. Reported from
  // production as green bean inventory "disappearing" after a work order: create a
  // real item, receive it on a PO, roast it once, then open the form again and the
  // item is no longer offered. Nothing was deleted; GET /inventory still returns it
  // (the backend only ever hides rows tagged is_onboarding_sample, and a
  // user-created item is never tagged).
  //
  // The first form usually still listed the bean because the store starts at
  // `state: null` — only `panelMinimized` is persisted — so a form that mounts
  // before hydrate() resolves reads the tour as inactive. Once the state lands the
  // effect re-runs and the bean drops out, which is why it looks like the second
  // work order broke it.
  const onboardingSamplePickers = onboardingRoastStep;
  // While the tour is running the pickers must offer only the tagged sample data,
  // never the user's real inventory — sampleRefs holds the exact sample ids.
  const sampleRefs = useOnboardingStore((s) => s.state?.sampleRefs);
  const localizedDefaultStages: RoastStageState[] = useMemo(() => [
    { key: 'start', label: t('roast.profile.stages.start'), time: '00:00', temp: '' },
    { key: 'turning_point', label: t('roast.profile.stages.turningPoint'), time: '', temp: '' },
    { key: 'yellow', label: t('roast.profile.stages.yellow'), time: '', temp: '' },
    { key: 'first_crack', label: t('roast.profile.stages.firstCrack'), time: '', temp: '' },
    { key: 'end', label: t('roast.profile.stages.end'), time: '', temp: '' },
  ], [t]);
  const [isLoading, setIsLoading] = useState(true);
  const [isArtisanProfileLoading, setIsArtisanProfileLoading] = useState(false);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [greenBeans, setGreenBeans] = useState<InventoryItem[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [roasters, setRoasters] = useState<Roaster[]>([]);
  const [manualInputWeight, setManualInputWeight] = useState('');
  const [manualOutputWeight, setManualOutputWeight] = useState('');
  const [isAutoCalculatingLots, setIsAutoCalculatingLots] = useState(false);

  const [selectedBeanId, setSelectedBeanId] = useState<string>('');
  const [lotOptions, setLotOptions] = useState<
    { id: string; name: string; remaining: number; density?: number | null; moisture?: number | null; processType?: string | null }[]
  >([]);
  const [lots, setLots] = useState<LotRow[]>([{ lotId: '', remaining: 0, usedWeight: undefined }]);

  const [useRoastProfile, setUseRoastProfile] = useState(false);
  const [selectedRecipeId, setSelectedRecipeId] = useState<string>('');
  const [useSnapshot, setUseSnapshot] = useState(false);

  // Artisan Profile Integration
  const [artisanProfiles, setArtisanProfiles] = useState<ArtisanProfileListItemDTO[]>([]);
  const [selectedArtisanProfileId, setSelectedArtisanProfileId] = useState<string>('');

  const [useArtisanProfile, setUseArtisanProfile] = useState(false);
  const [linkedArtisanProfileId, setLinkedArtisanProfileId] = useState<string>('');
  const [linkedArtisanProfileTitle, setLinkedArtisanProfileTitle] = useState<string>('');
  const [saveAsNewRecipe, setSaveAsNewRecipe] = useState(false);
  const [useStopwatchMode, setUseStopwatchMode] = useState(false);

  const [roastData, setRoastData] = useState<RoastData & { stageStates: RoastStageState[] }>({
    stages: [],
    weightInput: 0,
    weightOutput: 0,
    agtron: { wholeBean: '', groundBean: '', classification: { scaa: '', common: '' } },
    profile: [],
    stageStates: localizedDefaultStages,
  });
  const [roastType, setRoastType] = useState<string>('');
  const initialRoastTypeRef = React.useRef<string | null>(null);
  const isEditMode = !!initialData;
  const initialAsWorkOrder = initialData && isWorkOrder(initialData) ? initialData : undefined;
  const initialStatus = initialAsWorkOrder?.status;
  const isCompleted = initialStatus === 'completed';
  const workOrderId = initialAsWorkOrder?.id;
  const submitActionRef = React.useRef<'draft' | 'schedule' | 'complete'>(isEditMode ? 'schedule' : 'draft');

  const [stage, setStage] = useState<RoastStage>('idle');
  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const secondsRef = React.useRef(0);
  const stopwatchRef = React.useRef<HTMLDivElement | null>(null);
  const autosaveTimeoutRef = React.useRef<number | undefined>(undefined);
  const autosaveInFlightRef = React.useRef(false);
  const lastAutosavePayloadRef = React.useRef<string | null>(null);
  const pendingAutosavePayloadRef = React.useRef<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<BasicInfoFormData>({
    resolver: zodResolver(basicInfoSchema),
    defaultValues: {
      machine: initialData?.machine || '',
      roaster: initialData?.roaster || '',
      machineTypeId: initialData?.machineTypeId || '',
      roasterId: initialData?.roasterId || '',
      targetDate: initialData?.targetDate
        ? new Date(initialData.targetDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      time: initialData?.time || new Date().toTimeString().slice(0, 5),
      internalNotes: initialData?.internalNotes || '',
    },
  });

  const selectedMachineId = watch('machineTypeId');
  const selectedMachineName = watch('machine');
  const selectedMachine = useMemo(() => {
    if (selectedMachineId) {
      const byId = machines.find(m => m.id === selectedMachineId);
      if (byId) return byId;
    }
    if (selectedMachineName) {
      const byName = machines.find(m => m.name === selectedMachineName);
      if (byName) return byName;
    }
    return undefined;
  }, [machines, selectedMachineId, selectedMachineName]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Fetching and mapping live in workOrderReferenceData so the bulk
        // scheduling cards share exactly the same lists and shaping.
        const reference = await fetchWorkOrderReferenceData(onboardingSamplePickers);
        const recipesData = reference.recipes;
        const artisanData = reference.artisanProfiles;
        const beans = reference.greenBeans;
        const mappedMachines = reference.machines;
        const roasterArr = reference.roasters;

        setRecipes(recipesData);
        setArtisanProfiles(artisanData);
        setGreenBeans(onboardingSamplePickers ? beans.filter(b => b.isOnboardingSample) : beans);
        const data = initialAsWorkOrder;

        if (initialRoastTypeRef.current === null) {
          const resolvedInitial = resolveWorkOrderRoastType(
            data?.roastType,
            data?.roastProfile?.roastType,
          );
          initialRoastTypeRef.current = resolvedInitial;
        }

        if (data?.machine && mappedMachines.length > 0) {
          const matchedById = data.machineTypeId ? mappedMachines.find(m => m.id === data.machineTypeId) : undefined;
          const matchedByName = mappedMachines.find(m => m.name === data.machine);
          const resolvedMachine = matchedById || matchedByName;

          if (resolvedMachine && data.machineTypeId !== resolvedMachine.id) {
            setValue('machineTypeId', resolvedMachine.id, { shouldValidate: true });
          }
        }

        setMachines(onboardingSamplePickers ? mappedMachines.filter(m => m.id === sampleRefs?.machineTypeId) : mappedMachines);
        setRoasters(onboardingSamplePickers ? roasterArr.filter(r => r.id === sampleRefs?.cupperId) : roasterArr);

        if (data?.recipeId) {
          setUseRoastProfile(true);
          setUseSnapshot(true);
          setSelectedRecipeId(data.recipeId);
        }

        if (data?.useRoastProfile && data.roastProfile) {
          setUseRoastProfile(true);
          // The work order's own roast type wins over the profile snapshot. Reading the
          // snapshot here is what let an Artisan-imported value overwrite the roast type
          // the user picked, the moment the work order was saved again (WO-028).
          setRoastType(resolveWorkOrderRoastType(data.roastType, data.roastProfile.roastType));

          const stageSummary = data.roastProfile.stageSummary || {};

          setRoastData(prev => {
            const next = { ...prev };

            next.weightInput = data.inputWeight ?? prev.weightInput;
            next.weightOutput = data.outputWeight ?? prev.weightOutput;

            if (data.roastProfile?.agtron) {
              next.agtron = {
                wholeBean: data.roastProfile.agtron.wholeBean ? String(data.roastProfile.agtron.wholeBean) : '',
                groundBean: data.roastProfile.agtron.groundBean ? String(data.roastProfile.agtron.groundBean) : '',
                classification: {
                  scaa: data.roastProfile.agtron.classification?.scaa ?? '',
                  common: data.roastProfile.agtron.classification?.common ?? '',
                },
              };
            }

            if (data.roastProfile?.profile) {
              next.profile = data.roastProfile.profile as RoastProfileRow[];
            }

            next.stageStates = localizedDefaultStages.map(s => {
              const summary = stageSummary[s.key];
              const rawTemp = summary?.temp;
              const numericTemp = typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
              const safeTemp = numericTemp === undefined || Number.isNaN(numericTemp) ? s.temp : numericTemp;

              return {
                ...s,
                time: summary?.time ?? s.time,
                temp: safeTemp,
              };
            });

            return next;
          });
        }

        if (data?.artisanProfileId) {
          setLinkedArtisanProfileId(data.artisanProfileId);
          setLinkedArtisanProfileTitle(data.artisanProfileTitle || '');
        } else {
          setLinkedArtisanProfileId('');
          setLinkedArtisanProfileTitle('');
        }

        if (!data?.useRoastProfile && data?.roastType) {
          setRoastType(data.roastType || '');
        }

        if (data?.inputWeight !== undefined) {
          setManualInputWeight(String(data.inputWeight));
        }
        if (data?.outputWeight !== undefined) {
          setManualOutputWeight(String(data.outputWeight));
        }

        if (data?.itemId) {
          setSelectedBeanId(data.itemId);
        }
        if (data?.items && data.items.length > 0) {
          const firstItem = data.items[0];
          if (firstItem.lots && firstItem.lots.length > 0) {
            setLots(
              firstItem.lots.map(lot => ({
                lotId: lot.lotId,
                remaining: 0,
                usedWeight: typeof lot.quantity === 'number' ? lot.quantity : Number(lot.quantity) || undefined,
                originalUsedWeight: lot.quantity,
              }))
            );
          }
        }
      } catch (err) {
        console.error('Failed to load data', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [initialData, initialAsWorkOrder, setValue, localizedDefaultStages, onboardingSamplePickers, sampleRefs]);

  useEffect(() => {
    if (!selectedBeanId) {
      setLotOptions([]);
      return;
    }
    const loadLots = async () => {
      try {
        const fetchedLots = await inventoryService.getLots(selectedBeanId);
        const currentBean = greenBeans.find(b => b.id === selectedBeanId);
        const profiles = currentBean?.greenBeanProfiles || [];

        const isStockDeducted = initialStatus === 'scheduled' || initialStatus === 'completed';
        const workOrderData = initialAsWorkOrder;

        let usedLotsInWO: { lotId: string; quantity: number }[] = [];

        const initialItemId =
          workOrderData?.items && workOrderData.items.length > 0
            ? workOrderData.items[0].inventoryItemId
            : workOrderData?.itemId;

        if (initialItemId === selectedBeanId) {
          if (workOrderData?.items && workOrderData.items.length > 0) {
            const firstItem = workOrderData.items[0];
            if (firstItem.lots) {
              usedLotsInWO = firstItem.lots.map(l => ({ lotId: l.lotId, quantity: l.quantity }));
            }
          } else if (initialData && 'lots' in initialData && Array.isArray(initialData.lots)) {
            usedLotsInWO = (initialData.lots as { lotId: string; quantity: number }[]).map(l => ({
              lotId: l.lotId,
              quantity: l.quantity,
            }));
          }
        }

        const mappedLots = fetchedLots
          .map(l => {
            // A lot's green bean profile is linked by lot_id; its `type`
            // discriminator is not reliably 'LotType' (lot-specific profiles
            // are stored as 'ItemType' with a lot_id), so match by lot_id.
            const lotProfile = profiles.find(p => p.lot_id === l.id);

            const usedInThis = usedLotsInWO.find(ul => ul.lotId === l.id);
            const addBackQty = isStockDeducted && usedInThis ? usedInThis.quantity : 0;

            const processType = lotProfile?.process_type ?? null;
            return {
              id: l.id,
              name: buildLotOptionName(l.lotNumber, lotProfile, l.remainingQuantity + addBackQty, t),
              remaining: l.remainingQuantity + addBackQty,
              density: lotProfile?.density,
              moisture: lotProfile?.moisture_content,
              processType,
              hasGreenBeanProfile: !!lotProfile,
              greenBeanProfileId: lotProfile?.id,
            };
          })
          .filter(l => l.remaining > 0.0001 || usedLotsInWO.some(ul => ul.lotId === l.id));

        const missingLots = usedLotsInWO
          .filter(ul => ul.lotId && !mappedLots.some(m => m.id === ul.lotId))
          .map(ul => {
            const effectiveRemaining = isStockDeducted ? ul.quantity : 0;

            return {
              id: ul.lotId,
              name: `${t('workOrders.form.unknownLot')} ${ul.lotId.substring(0, 8)}... (${t('workOrders.form.used')}: ${ul.quantity}${t('inventory.history.unitKg')})`,
              remaining: effectiveRemaining,
              density: undefined as number | null | undefined,
              moisture: undefined as number | null | undefined,
              processType: null as string | null,
              hasGreenBeanProfile: false,
              greenBeanProfileId: undefined as string | undefined,
            };
          });

        const finalLotOptions = [...mappedLots, ...missingLots];
        setLotOptions(finalLotOptions);

        setLots(prev =>
          prev.map(row => {
            if (!row.lotId) return row;
            const match = finalLotOptions.find(l => l.id === row.lotId);
            if (!match) return row;
            return { ...row, remaining: match.remaining };
          })
        );
      } catch (err) {
        console.error('Failed to fetch lots', err);
      }
    };
    loadLots();
  }, [selectedBeanId, greenBeans, initialData, initialAsWorkOrder, initialStatus, t]);

  const handleProfileChange = useCallback(
    (profile: RoastProfileRow[]) => {
      setRoastData(prev => ({ ...prev, profile }));
    },
    [setRoastData]
  );

  const handleArtisanProfileSelect = useCallback(async (profileId: string) => {
    setSelectedArtisanProfileId(profileId);
    if (!profileId) {
      setWarningMessage(null);
      return;
    }

    try {
      setIsArtisanProfileLoading(true);
      const fullProfile = await artisanProfileService.getById(profileId);
      if (fullProfile.recipe) {
        setUseRoastProfile(true);
        setUseSnapshot(false);

        const stageSummary = fullProfile.recipe.stage_summary || {};

        setRoastData(prev => {
          const next = { ...prev };

          if (fullProfile.recipe?.profile) {
            next.profile = fullProfile.recipe.profile as RoastProfileRow[];
          }

          next.stageStates = localizedDefaultStages.map(s => {
            const summary = stageSummary[s.key];
            const rawTemp = summary?.temp;
            const numericTemp = typeof rawTemp === 'string' ? Number(rawTemp) : rawTemp;
            const safeTemp = numericTemp === undefined || Number.isNaN(numericTemp) ? s.temp : numericTemp;
            return {
              ...s,
              time: summary?.time ?? s.time,
              temp: safeTemp,
            };
          });
          return next;
        });
      }
    } catch (err) {
      console.error(t('workOrders.page.errors.fetchFailed'), err);
      setWarningMessage(t('workOrders.form.errors.loadArtisanProfileDetailsFailed'));
    } finally {
      setIsArtisanProfileLoading(false);
    }
  }, [localizedDefaultStages, t]);

  const handleStageChange = useCallback(
    (idx: number, field: keyof RoastStageState, val: string | number) => {
      setRoastData(prev => {
        const newStages = [...prev.stageStates];
        newStages[idx] = { ...newStages[idx], [field]: val };
        return { ...prev, stageStates: newStages };
      });
    },
    [setRoastData]
  );

  useEffect(() => {
    if (!useRoastProfile) return;
    setRoastData(prev => ({
      ...prev,
      weightInput: manualInputWeight === '' ? prev.weightInput : Number(manualInputWeight || prev.weightInput || 0),
      weightOutput:
        manualOutputWeight === '' ? prev.weightOutput : Number(manualOutputWeight || prev.weightOutput || 0),
    }));
  }, [useRoastProfile, manualInputWeight, manualOutputWeight]);

  useEffect(() => {
    if (!useSnapshot) return;
    if (!useStopwatchMode) return;
    if (!stopwatchRef.current) return;

    stopwatchRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [useSnapshot, useStopwatchMode]);

  useEffect(() => {
    if (!isRunning) return;

    let rafId: number;
    let lastTimestamp: number | null = null;
    // React-state `seconds` only mirrors the ref when a new 30s bucket is
    // crossed. This keeps consumers of the prop (e.g. RoastProfile's 30s row
    // insertion) correct while avoiding a full form re-render every second.
    let lastPublishedBucket = Math.floor(secondsRef.current / 30);

    const update = (timestamp: number) => {
      if (lastTimestamp !== null) {
        const delta = (timestamp - lastTimestamp) / 1000;
        secondsRef.current += delta;
        publishStopwatchTick(secondsRef.current);

        const currentBucket = Math.floor(secondsRef.current / 30);
        if (currentBucket > lastPublishedBucket) {
          lastPublishedBucket = currentBucket;
          setSeconds(secondsRef.current);
        }
      }
      lastTimestamp = timestamp;
      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isRunning, secondsRef]);

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = Math.floor(totalSeconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const updateStageTime = useCallback((key: string, timeStr: string, temp?: number) => {
    setRoastData(prev => ({
      ...prev,
      stageStates: prev.stageStates.map(s =>
        s.key === key ? { ...s, time: timeStr, ...(temp === undefined ? {} : { temp }) } : s
      ),
    }));
  }, []);

  const buildStageSummary = useCallback((stageStates: RoastStageState[]) => {
    return stageStates.reduce(
      (acc, s) => ({
        ...acc,
        [s.key]: { time: s.time, temp: s.temp },
      }),
      {} as Record<string, { time?: string; temp?: string | number }>
    );
  }, []);

  const parseTimeToMinutes = useCallback((value: string | undefined) => {
    if (!value) return 0;
    const parts = value.split(':');
    if (parts.length !== 2) return 0;
    const minutes = Number(parts[0]);
    const secondsValue = Number(parts[1]);
    if (Number.isNaN(minutes) || Number.isNaN(secondsValue)) return 0;
    return minutes + secondsValue / 60;
  }, []);

  const buildAutosavePayload = useCallback(
    (overrides?: {
      stageStates?: RoastStageState[];
      profile?: RoastProfileRow[];
      lots?: LotRow[];
      roastType?: string;
      inputWeight?: string;
      outputWeight?: string;
    }) => {
      const stageStates = overrides?.stageStates ?? roastData.stageStates;
      const profile = overrides?.profile ?? roastData.profile;
      const roastTypeValue = overrides?.roastType ?? roastType;
      const resolvedInputWeight = overrides?.inputWeight ?? manualInputWeight;
      const resolvedOutputWeight = overrides?.outputWeight ?? manualOutputWeight;
      const inputWeightValue = useRoastProfile
        ? Number(roastData.weightInput || 0)
        : Number(resolvedInputWeight === '' ? 0 : resolvedInputWeight);
      const outputWeightValue = useRoastProfile
        ? Number(roastData.weightOutput || 0)
        : Number(resolvedOutputWeight === '' ? 0 : resolvedOutputWeight);
      const lotsValue = overrides?.lots ?? lots;
      const validLots = selectedBeanId ? lotsValue.filter(l => l.lotId && Number(l.usedWeight || 0) > 0) : [];
      const resolvedArtisanProfileId = linkedArtisanProfileId || selectedArtisanProfileId || undefined;

      const startTime = stageStates.find(s => s.key === 'start')?.time;
      const endTime = stageStates.find(s => s.key === 'end')?.time;
      const totalTimeMinutes = Math.max(parseTimeToMinutes(endTime) - parseTimeToMinutes(startTime), 0);
      const shrinkagePercent =
        inputWeightValue > 0 && outputWeightValue > 0 ? ((inputWeightValue - outputWeightValue) / inputWeightValue) * 100 : 0;

      const stageSummary = buildStageSummary(stageStates);

      const agtronPayload =
        roastData.agtron.wholeBean !== '' || roastData.agtron.groundBean !== ''
          ? {
            wholeBean: Number(roastData.agtron.wholeBean || 0),
            groundBean: Number(roastData.agtron.groundBean || 0),
            classification: roastData.agtron.classification,
          }
          : undefined;

      const roastProfilePayload = useRoastProfile
        ? {
          agtron: agtronPayload,
          roastType: roastTypeValue,
          stageSummary,
          profile,
          weightAnalysis: {
            shrinkagePercent,
            totalTimeMinutes,
            inputWeight: inputWeightValue,
            outputWeight: outputWeightValue,
          },
        }
        : undefined;

      return {
        roastProfile: roastProfilePayload,
        lots: validLots.map(l => ({
          itemId: selectedBeanId,
          lotId: l.lotId,
          quantity: Number(l.usedWeight),
        })),
        roastType: roastTypeValue?.trim() || undefined,
        artisanProfileId: resolvedArtisanProfileId,
        inputWeight: inputWeightValue,
        outputWeight: outputWeightValue,
      };
    },
    [
      roastData.stageStates,
      roastData.profile,
      roastData.agtron,
      roastData.weightInput,
      roastData.weightOutput,
      roastType,
      manualInputWeight,
      manualOutputWeight,
      lots,
      selectedBeanId,
      linkedArtisanProfileId,
      selectedArtisanProfileId,
      useRoastProfile,
      buildStageSummary,
      parseTimeToMinutes,
    ]
  );

  const canAutosave =
    isEditMode &&
    !!workOrderId &&
    initialStatus !== 'completed' &&
    initialStatus !== 'cancelled' &&
    (initialStatus === 'scheduled' || (initialStatus === 'draft' && useRoastProfile));

  const applyLinkedArtisanProfileState = useCallback(
    (profileId?: string, fallbackTitle?: string) => {
      const normalizedId = profileId || '';
      if (!normalizedId) {
        setLinkedArtisanProfileId('');
        setLinkedArtisanProfileTitle('');
        return;
      }
      const matched = artisanProfiles.find(p => p.id === normalizedId);
      setLinkedArtisanProfileId(normalizedId);
      setLinkedArtisanProfileTitle(matched?.title || fallbackTitle || '');
      setUseArtisanProfile(false);
    },
    [artisanProfiles]
  );

  const executeAutosave = useCallback(
    async (payload: ReturnType<typeof buildAutosavePayload>, serialized: string) => {
      if (!canAutosave || !workOrderId) return;
      if (autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      setSaving(true);
      setSaved(false);
      try {
        const updatedWorkOrder = await workOrderService.autosave(workOrderId, payload);
        applyLinkedArtisanProfileState(
          updatedWorkOrder.artisanProfileId || payload.artisanProfileId,
          updatedWorkOrder.artisanProfileTitle
        );
        lastAutosavePayloadRef.current = serialized;
        setSaved(true);
      } catch (error) {
        console.error('Autosave failed', error);
        setSaved(false);
      } finally {
        setSaving(false);
        autosaveInFlightRef.current = false;
        pendingAutosavePayloadRef.current = null;
      }
    },
    [applyLinkedArtisanProfileState, canAutosave, workOrderId]
  );

  const autosavePayload = useMemo(() => buildAutosavePayload(), [buildAutosavePayload]);
  const [autosaveSerialized, setAutosaveSerialized] = useState<string>('');

  useEffect(() => {
    if (!canAutosave || isLoading) return;
    const handle = window.setTimeout(() => {
      setAutosaveSerialized(JSON.stringify(autosavePayload));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [autosavePayload, canAutosave, isLoading]);

  useEffect(() => {
    if (!canAutosave || isLoading) return;
    if (lastAutosavePayloadRef.current) return;
    lastAutosavePayloadRef.current = JSON.stringify(buildAutosavePayload());
    setSaved(true);
  }, [canAutosave, isLoading, buildAutosavePayload]);

  useEffect(() => {
    if (!canAutosave || isLoading) return;
    if (autosaveSerialized === lastAutosavePayloadRef.current) return;
    if (pendingAutosavePayloadRef.current === autosaveSerialized) return;
    setSaved(false);
    if (autosaveTimeoutRef.current) {
      window.clearTimeout(autosaveTimeoutRef.current);
    }
    pendingAutosavePayloadRef.current = autosaveSerialized;
    autosaveTimeoutRef.current = window.setTimeout(() => {
      void executeAutosave(autosavePayload, autosaveSerialized);
    }, 1500);

    return () => {
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, [autosavePayload, autosaveSerialized, canAutosave, executeAutosave, isLoading]);

  const triggerAutosaveWithStageUpdate = useCallback(
    (stageKey: string, timeStr: string, temp?: number) => {
      if (!canAutosave) return;
      const nextStageStates = roastData.stageStates.map(s =>
        s.key === stageKey ? { ...s, time: timeStr, ...(temp === undefined ? {} : { temp }) } : s
      );
      const payload = buildAutosavePayload({ stageStates: nextStageStates });
      const serialized = JSON.stringify(payload);
      if (autosaveTimeoutRef.current) {
        window.clearTimeout(autosaveTimeoutRef.current);
      }
      pendingAutosavePayloadRef.current = serialized;
      void executeAutosave(payload, serialized);
    },
    [canAutosave, roastData.stageStates, buildAutosavePayload, executeAutosave]
  );

  const handleStopwatchMainClick = (atSeconds: number, beanTemp: number) => {
    const currentTime = formatTime(atSeconds);

    if (stage === 'idle') {
      setStage('turning');
      setIsRunning(true);
      updateStageTime('start', '00:00', beanTemp);
      triggerAutosaveWithStageUpdate('start', '00:00', beanTemp);
    } else if (stage === 'turning') {
      setStage('yellow');
      updateStageTime('turning_point', currentTime, beanTemp);
      triggerAutosaveWithStageUpdate('turning_point', currentTime, beanTemp);
    } else if (stage === 'yellow') {
      setStage('firstCrack');
      updateStageTime('yellow', currentTime, beanTemp);
      triggerAutosaveWithStageUpdate('yellow', currentTime, beanTemp);
    } else if (stage === 'firstCrack') {
      setStage('endReady');
      updateStageTime('first_crack', currentTime, beanTemp);
      triggerAutosaveWithStageUpdate('first_crack', currentTime, beanTemp);
    } else if (stage === 'endReady') {
      setStage('ended');
      setIsRunning(false);
      updateStageTime('end', currentTime, beanTemp);
      triggerAutosaveWithStageUpdate('end', currentTime, beanTemp);
    }
  };

  const handleStopwatchStart = useCallback(() => {
    setIsRunning(true);
  }, []);

  const handleStopwatchPause = useCallback(() => {
    setIsRunning(false);
  }, []);

  const handleStopwatchRestart = () => {
    secondsRef.current = 0;
    setSeconds(0);
    resetStopwatchTick();
    setStage('idle');
    setIsRunning(false);

    setRoastData(prev => ({
      ...prev,
      stageStates: prev.stageStates.map(s => ({
        ...s,
        time: s.key === 'start' ? '00:00' : '',
        temp: '',
      })),
    }));
  };

  const handleWeightInputChange = (val: number | undefined) => {
    setRoastData(prev => ({ ...prev, weightInput: val }));
    setManualInputWeight(val === undefined ? '' : val.toString());
  };

  const handleWeightOutputChange = (val: number | undefined) => {
    setRoastData(prev => ({ ...prev, weightOutput: val }));
    setManualOutputWeight(val === undefined ? '' : val.toString());
  };

  const handleAgtronChange = useCallback((field: 'whole' | 'ground', val: string) => {
    setRoastData(prev => ({
      ...prev,
      agtron: { ...prev.agtron, [field === 'whole' ? 'wholeBean' : 'groundBean']: val },
    }));
  }, []);

  const handleRoastTypeChange = useCallback((val: string) => {
    setRoastType(val);
  }, []);

  const handleLotChange = (index: number, lotId: string) => {
    const selected = lotOptions.find(l => l.id === lotId);
    const updated = [...lots];

    const parsedTarget = Number(manualInputWeight || 0);
    const targetWeight = Number.isFinite(parsedTarget) ? parsedTarget : 0;
    const totalOtherUsedWeight = updated.reduce((sum, row, idx) => {
      if (idx === index) return sum;
      const numeric = Number(row.usedWeight || 0);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
    const remainingTarget = Math.max(targetWeight - totalOtherUsedWeight, 0);
    const remainingInLot = Number(selected?.remaining || 0);
    const autoUsedWeight =
      lotId && remainingTarget > 0 && remainingInLot > 0
        ? Number(Math.min(remainingTarget, remainingInLot).toFixed(3))
        : undefined;

    updated[index] = {
      ...updated[index],
      lotId,
      remaining: selected?.remaining || 0,
      usedWeight: autoUsedWeight,
    };
    setLots(updated);
  };

  const handleWeightChange = (index: number, value: number | undefined) => {
    const updated = [...lots];
    updated[index].usedWeight = value;
    setLots(updated);

    const totalUsedWeight = updated.reduce((sum, row) => {
      const numeric = Number(row.usedWeight || 0);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
    const roundedTotal = Number(totalUsedWeight.toFixed(3));
    const nextManualInput = roundedTotal > 0 ? roundedTotal.toString() : '';
    setManualInputWeight(nextManualInput);
    setRoastData(prev => ({ ...prev, weightInput: roundedTotal > 0 ? roundedTotal : undefined }));
  };

  const addLotRow = () => setLots([...lots, { lotId: '', remaining: 0, usedWeight: undefined }]);

  const removeLotRow = (index: number) => {
    const updated = lots.filter((_, i) => i !== index);
    setLots(updated);

    const totalUsedWeight = updated.reduce((sum, row) => {
      const numeric = Number(row.usedWeight || 0);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);
    const roundedTotal = Number(totalUsedWeight.toFixed(3));
    const nextManualInput = roundedTotal > 0 ? roundedTotal.toString() : '';
    setManualInputWeight(nextManualInput);
    setRoastData(prev => ({ ...prev, weightInput: roundedTotal > 0 ? roundedTotal : undefined }));
  };

  const handleBeanChange = (value: string) => {
    setSelectedBeanId(value);
    setSelectedRecipeId('');
    setLots([{ lotId: '', remaining: 0, usedWeight: undefined }]);
  };

  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const handleArtisanProfileUnlinked = useCallback((updatedWorkOrder?: Partial<WorkOrder>) => {
    setLinkedArtisanProfileId(updatedWorkOrder?.artisanProfileId || '');
    setLinkedArtisanProfileTitle(updatedWorkOrder?.artisanProfileTitle || '');
    setSelectedArtisanProfileId('');
    setUseArtisanProfile(false);
    // The work order no longer carries a roast profile, and the roast data below is wiped
    // with it. Leaving this set kept the debounced autosave alive and still sending a
    // roast_profile, which the autosave handler answers by writing use_roast_profile back
    // to TRUE — taking the work order out of the "Sync to WO" list for good (AS-006).
    setUseRoastProfile(false);
    setUseSnapshot(false);
    setUseStopwatchMode(false);
    setRoastData(prev => ({
      ...prev,
      stages: [],
      agtron: { wholeBean: '', groundBean: '', classification: { scaa: '', common: '' } },
      profile: [],
      stageStates: localizedDefaultStages,
    }));
    setWarningMessage(null);
  }, [localizedDefaultStages]);

  const handleAutoCalculateLots = async () => {
    if (!selectedBeanId || !manualInputWeight) return;
    const weight = Number(manualInputWeight);
    if (!Number.isFinite(weight) || weight <= 0) return;

    try {
      setIsAutoCalculatingLots(true);

      const availableLots = lotOptions.map(l => ({
        id: l.id,
        name: l.name,
        remaining: l.remaining,
      }));

      const result = calculateAllocation(weight, availableLots);

      if (result.warning) {
        setWarningMessage(result.warning);
      }

      if (result.lots.length > 0) {
        setLots(result.lots.map(l => ({ ...l, usedWeight: Number(l.usedWeight) })));
      }
    } catch (error) {
      console.error(t('workOrders.form.errors.autoCalculateLotsFailed'), error);
      setWarningMessage(t('workOrders.form.errors.allocationErrorOccurred'));
    } finally {
      setIsAutoCalculatingLots(false);
    }
  };

  const totalUsedWeight = lots.reduce((sum, l) => {
    const numeric = Number(l.usedWeight || 0);
    return Number.isFinite(numeric) ? sum + numeric : sum;
  }, 0);

  const parsedRoastInputWeight = Number(manualInputWeight || 0);
  const hasRoastInputWeight = Number.isFinite(parsedRoastInputWeight) && parsedRoastInputWeight > 0;
  const parsedRoastOutputWeight = Number(manualOutputWeight || 0);
  const hasRoastOutputWeight = Number.isFinite(parsedRoastOutputWeight) && parsedRoastOutputWeight > 0;
  const inputWeightForOutputValidation = useRoastProfile ? Number(roastData.weightInput || 0) : parsedRoastInputWeight;
  const outputWeightForOutputValidation = useRoastProfile
    ? Number(roastData.weightOutput || 0)
    : parsedRoastOutputWeight;
  const isOutputExceedsInput =
    inputWeightForOutputValidation > 0 &&
    outputWeightForOutputValidation > 0 &&
    outputWeightForOutputValidation > inputWeightForOutputValidation;
  const hasLotsUsedWeight = lots.some(l => {
    if (l.usedWeight === undefined) return false;
    const numeric = Number(l.usedWeight);
    return Number.isFinite(numeric) && numeric > 0;
  });
  const isWeightMismatch =
    hasRoastInputWeight && hasLotsUsedWeight && Math.abs(totalUsedWeight - parsedRoastInputWeight) > WEIGHT_TOLERANCE;
  const canSaveAsComplete =
    ((isEditMode && (initialStatus === 'draft' || initialStatus === 'scheduled')) ||
      // Onboarding step 3 guides the user to complete a brand-new (not-yet-saved)
      // work order in one go; the create page handles create→scheduled→completed.
      (!isEditMode && onboardingActive)) &&
    hasRoastInputWeight &&
    hasRoastOutputWeight;

  const [pendingSubmitData, setPendingSubmitData] = useState<PendingSubmitData | null>(null);

  const [isMismatchModalOpen, setIsMismatchModalOpen] = useState(false);
  const [isOutputGreaterModalOpen, setIsOutputGreaterModalOpen] = useState(false);
  const [isCompleteSummaryModalOpen, setIsCompleteSummaryModalOpen] = useState(false);

  const proceedSubmit = useCallback(
    (nextSubmitData?: PendingSubmitData) => {
      const submitData = nextSubmitData ?? pendingSubmitData;
      if (!submitData) return;

      try {
        const { basicInfo, validLots, inputWeight, outputWeight, outputExpiryDate } = submitData;

        const stageSummary = roastData.stageStates.reduce(
          (acc, s) => ({
            ...acc,
            [s.key]: { time: s.time, temp: s.temp },
          }),
          {} as Record<string, { time?: string; temp?: string | number }>
        );

        const parseTimeToMinutesLocal = (value: string | undefined) => {
          if (!value) return 0;
          const parts = value.split(':');
          if (parts.length !== 2) return 0;
          const minutes = Number(parts[0]);
          const secondsValue = Number(parts[1]);
          if (Number.isNaN(minutes) || Number.isNaN(secondsValue)) return 0;
          return minutes + secondsValue / 60;
        };

        const startTime = roastData.stageStates.find(s => s.key === 'start')?.time;
        const endTime = roastData.stageStates.find(s => s.key === 'end')?.time;
        const totalTimeMinutes = Math.max(parseTimeToMinutesLocal(endTime) - parseTimeToMinutesLocal(startTime), 0);

        const shrinkagePercent =
          inputWeight > 0 && outputWeight > 0 ? ((inputWeight - outputWeight) / inputWeight) * 100 : 0;

        const lotsPayload =
          selectedBeanId && validLots.length > 0
            ? validLots.map(l => ({
              itemId: selectedBeanId,
              lotId: l.lotId,
              quantity: Number(l.usedWeight),
            }))
            : [];

        if (useRoastProfile && !roastType.trim()) {
          setWarningMessage(t('workOrders.form.errors.roastTypeRequiredWhenUsingProfile'));
          return;
        }

        if (!useRoastProfile && (selectedBeanId || manualInputWeight) && !roastType.trim()) {
          setWarningMessage(t('workOrders.form.errors.roastTypeRequired'));
          return;
        }

        const agtronPayload =
          roastData.agtron.wholeBean !== '' || roastData.agtron.groundBean !== ''
            ? {
              wholeBean: Number(roastData.agtron.wholeBean || 0),
              groundBean: Number(roastData.agtron.groundBean || 0),
              classification: roastData.agtron.classification,
            }
            : undefined;

        const roastProfilePayload =
          useRoastProfile
            ? {
              agtron: agtronPayload,
              roastType,
              stageSummary,
              profile: roastData.profile,
              weightAnalysis: {
                shrinkagePercent,
                totalTimeMinutes,
                inputWeight,
                outputWeight,
              },
            }
            : undefined;

        const workOrderData: CreateWorkOrderData = {
          machine: basicInfo.machine,
          roaster: basicInfo.roaster,
          machineTypeId: basicInfo.machineTypeId,
          roasterId: basicInfo.roasterId,
          date: basicInfo.targetDate,
          time: basicInfo.time,
          itemId: selectedBeanId,
          inputWeight,
          outputWeight,
          useRoastProfile,
          artisanProfileId: linkedArtisanProfileId || selectedArtisanProfileId || undefined,
          roastType: roastType.trim() || undefined,
          lots: lotsPayload,
          roastProfile: roastProfilePayload,
          saveAsNewRecipe,
          internalNotes: basicInfo.internalNotes,
          outputExpiryDate,
        };

        setIsOutputGreaterModalOpen(false);
        const action = submitActionRef.current;

        // Wrap original onSubmit to inject sync logic
        const originalOnSubmit = onSubmit;
        originalOnSubmit(workOrderData, action);
        applyLinkedArtisanProfileState(workOrderData.artisanProfileId, linkedArtisanProfileTitle);

        // Note: the component using this hook must handle the actual sync call
        // since `onSubmit` is passed in and usually redirects. 
        // For Phase 1, we will return selectedArtisanProfileId to let CreateWorkOrder page handle it if needed.
      } finally {
        setPendingSubmitData(null);
        setIsMismatchModalOpen(false);
      }
    },
    [
      pendingSubmitData,
      roastData,
      selectedBeanId,
      useRoastProfile,
      linkedArtisanProfileId,
      selectedArtisanProfileId,
      roastType,
      saveAsNewRecipe,
      manualInputWeight,
      linkedArtisanProfileTitle,
      onSubmit,
      applyLinkedArtisanProfileState,
      t,
    ]
  );

  const isRoastTypeDirty =
    initialStatus === 'completed' &&
    initialRoastTypeRef.current !== null &&
    (roastType || '').trim() !== (initialRoastTypeRef.current || '').trim() &&
    (roastType || '').trim() !== '';

  const commitRoastTypeAsInitial = useCallback((value: string) => {
    initialRoastTypeRef.current = value;
  }, []);

  const confirmCompleteSubmit = useCallback((expiryDate?: string) => {
    setIsCompleteSummaryModalOpen(false);
    if (pendingSubmitData) {
      proceedSubmit({ ...pendingSubmitData, outputExpiryDate: expiryDate });
    }
  }, [pendingSubmitData, proceedSubmit]);

  const cancelCompleteSummary = useCallback(() => {
    setIsCompleteSummaryModalOpen(false);
    setPendingSubmitData(null);
  }, []);

  const onFormSubmit = async (data: BasicInfoFormData) => {
    try {
      if (initialStatus === 'cancelled') return;
      if (initialStatus === 'completed' && !saveAsNewRecipe) return;
      const isScheduling = isEditMode && initialStatus === 'draft';
      const validLots = lots.filter(l => l.lotId && l.usedWeight && parseFloat(l.usedWeight.toString()) > 0);

      if (isScheduling && validLots.length === 0) {
        setWarningMessage(t('workOrders.form.errors.selectAtLeastOneLotBeforeScheduling'));
        return;
      }
      if (!isScheduling && selectedBeanId && validLots.length === 0) {
        setWarningMessage(t('workOrders.form.errors.selectAtLeastOneLotWithQuantityIfBeanSelected'));
        return;
      }

      const hasInsufficientStock = validLots.some(l => {
        const used = Number(l.usedWeight || 0);
        return used > l.remaining + 0.0001;
      });

      if (hasInsufficientStock) {
        setWarningMessage(t('workOrders.form.errors.lotsExceedRemainingStock'));
        return;
      }

      const wInProfile = Number(roastData.weightInput);
      const wOutProfile = Number(roastData.weightOutput);
      const parsedManualIn = Number(manualInputWeight || 0);
      const parsedManualOut = Number(manualOutputWeight || 0);

      const inputWeight = useRoastProfile ? wInProfile : parsedManualIn;
      const outputWeight = useRoastProfile ? wOutProfile : parsedManualOut;

      const totalUsedFromLots = validLots.reduce((sum, l) => {
        const numeric = Number(l.usedWeight);
        return Number.isFinite(numeric) ? sum + numeric : sum;
      }, 0);

      const nextSubmitData = {
        basicInfo: data,
        validLots,
        inputWeight,
        outputWeight,
        totalUsedFromLots,
      };
      setPendingSubmitData(nextSubmitData);

      if (inputWeight > 0 && totalUsedFromLots > 0 && Math.abs(totalUsedFromLots - inputWeight) > WEIGHT_TOLERANCE) {
        setIsMismatchModalOpen(true);
        return;
      }

      if (inputWeight > 0 && outputWeight > inputWeight) {
        setIsOutputGreaterModalOpen(true);
        return;
      }

      if (submitActionRef.current === 'complete') {
        setIsCompleteSummaryModalOpen(true);
        return;
      }

      proceedSubmit(nextSubmitData);
    } catch (error) {
      console.error(t('workOrders.page.errors.createFailed'), error);
      setWarningMessage(t('workOrders.form.errors.createFailedCheckInputs'));
    }
  };

  return {
    register,
    handleSubmit,
    control,
    setValue,
    errors,
    isSubmitting,
    isLoading,
    recipes,
    greenBeans,
    machines,
    roasters,
    setMachines,
    setRoasters,
    manualInputWeight,
    manualOutputWeight,
    isAutoCalculatingLots,
    selectedBeanId,
    setSelectedBeanId,
    lotOptions,
    lots,
    setLots,
    useRoastProfile,
    setUseRoastProfile,
    selectedRecipeId,
    setSelectedRecipeId,
    artisanProfiles,
    selectedArtisanProfileId,
    linkedArtisanProfileId,
    linkedArtisanProfileTitle,
    handleArtisanProfileSelect,
    handleArtisanProfileUnlinked,
    useArtisanProfile,
    setUseArtisanProfile,
    useSnapshot,
    setUseSnapshot,
    saveAsNewRecipe,
    setSaveAsNewRecipe,
    useStopwatchMode,
    setUseStopwatchMode,
    roastData,
    roastType,
    setRoastType,
    isEditMode,
    initialStatus,
    isCompleted,
    submitActionRef,
    stage,
    seconds,
    isRunning,
    stopwatchRef,
    saving,
    saved,
    canAutosave,
    handleProfileChange,
    handleStageChange,
    handleWeightInputChange,
    handleWeightOutputChange,
    handleAgtronChange,
    handleRoastTypeChange,
    handleStopwatchMainClick,
    handleStopwatchStart,
    handleStopwatchRestart,
    handleAutoCalculateLots,
    handleLotChange,
    handleWeightChange,
    addLotRow,
    removeLotRow,
    handleBeanChange,
    totalUsedWeight,
    parsedRoastInputWeight,
    parsedRoastOutputWeight,
    isWeightMismatch,
    isOutputExceedsInput,
    canSaveAsComplete,
    onboardingActive,
    onboardingRoastStep,
    pendingSubmitData,
    setPendingSubmitData,
    isMismatchModalOpen,
    setIsMismatchModalOpen,
    isOutputGreaterModalOpen,
    setIsOutputGreaterModalOpen,
    isCompleteSummaryModalOpen,
    setIsCompleteSummaryModalOpen,
    confirmCompleteSubmit,
    cancelCompleteSummary,
    isRoastTypeDirty,
    initialRoastType: initialRoastTypeRef.current,
    commitRoastTypeAsInitial,
    warningMessage,
    setWarningMessage,
    onFormSubmit,
    selectedMachine,
    isArtisanProfileLoading,
    handleStopwatchPause,
  };
};
